/** Filter value meaning "no assignee" or "no category". GitHub logins and option IDs never contain parentheses. */
export const NO_VALUE = '(none)';

/** The sprint board's URL state. Absent keys mean "no filter" (or the default board). */
export interface SprintBoardSearch {
  /** Logins, or `NO_VALUE` for unassigned issues. */
  assignees?: string[];
  /** Category option IDs, or `NO_VALUE` for issues without one. */
  categories?: string[];
  /** Show (or hide) lanes for iterations that have ended. Left out: `SwitchDefaults.past`. */
  past?: boolean;
  /** Hide the Backlog lane, which shows by default. */
  backlog?: false;
  /** Show (or hide) done issues: closed, or merged pull requests. Left out: `SwitchDefaults.done`. */
  done?: boolean;
  /** Board number; defaults to the first eligible board. */
  project?: number;
}

function canonicalStrings(value: unknown): string[] | undefined {
  const list: unknown[] = Array.isArray(value) ? value : [value];
  const strings = list
    .map((entry) => (typeof entry === 'number' ? String(entry) : entry))
    .filter((entry): entry is string => typeof entry === 'string')
    .map((entry) => entry.trim())
    .filter((entry) => entry.length > 0);
  const unique = [...new Set(strings)];
  return unique.length > 0 ? unique : undefined;
}

/**
 * Keep only well-formed values: deduped non-empty strings, `past` and `done` only as booleans, `backlog` only when
 * false, a positive whole board number.
 */
export function validateSprintBoardSearch(search: Record<string, unknown>): SprintBoardSearch {
  const assignees = canonicalStrings(search.assignees);
  const categories = canonicalStrings(search.categories);
  const { project } = search;
  return {
    ...(assignees && { assignees }),
    ...(categories && { categories }),
    ...(typeof search.past === 'boolean' && { past: search.past }),
    ...(search.backlog === false && { backlog: false }),
    ...(typeof search.done === 'boolean' && { done: search.done }),
    ...(typeof project === 'number' && Number.isInteger(project) && project > 0 && { project }),
  };
}

/** The Past and Done switches when the URL doesn't set them. */
export interface SwitchDefaults {
  done: boolean;
  past: boolean;
}

/**
 * Past off and Done on, except for a viewer with old issues to clean up (still open in an ended iteration): they
 * start with Past on and Done off, which shows them those issues and leaves out what's finished.
 */
export function switchDefaults(viewerHasOldIssues: boolean): SwitchDefaults {
  return { done: !viewerHasOldIssues, past: viewerHasOldIssues };
}

/** The search with the Past and Done switches as they stand: the URL's, else the defaults. */
export function withSwitchDefaults(
  search: SprintBoardSearch,
  defaults: SwitchDefaults,
): SprintBoardSearch & SwitchDefaults {
  return { ...search, done: search.done ?? defaults.done, past: search.past ?? defaults.past };
}

/** Apply a change (e.g. `{ assignees: [] }` to clear a filter) and drop whatever becomes empty. */
export function sprintBoardSearchWithPatch(
  search: SprintBoardSearch,
  patch: Partial<Record<keyof SprintBoardSearch, unknown>>,
): SprintBoardSearch {
  return validateSprintBoardSearch({ ...search, ...patch });
}

/** The assignee filter after picking one person (or `NO_VALUE`): just them, or none when it already was just them. */
export function toggledSoloAssignee(search: SprintBoardSearch, assignee: string): string[] | undefined {
  const { assignees } = search;
  return assignees?.length === 1 && assignees[0] === assignee ? undefined : [assignee];
}

export function hasSprintBoardFilters(search: SprintBoardSearch): boolean {
  return Boolean(search.assignees || search.categories);
}
